import { ErrorApi, ErrorNotLoggedIn, Preloader } from 'src/components'
import { savedTokens } from 'src/auth'
import Kvps from '../Info/Kvps'
import { useGetOrganization } from '../Info/queries'
import {
  TrackedNumberField,
  TrackedTextField,
  TrackedSaveButton,
  TrackedToggle,
  TrackedCountryCodeField,
} from '../Info/TrackedInputs'
import {
  savedSplashAndroid1,
  savedSplashAndroid2,
  savedSplashIos1,
  savedSplashIos2,
  savedLogoutOnLogin,
  savedAutoReadyLogin,
  savedActivityTimeout,
  savedCountryCode,
  savedDisabledAutoMarkdone,
  savedCrmButtonKey,
  savedCrmButtonEnabled,
} from '../Info/storage'
import Section from 'src/components/Shared/UIComponents/Section'
import SplashscreenImage from 'src/images/splash_screen_example.png'
import AmazonKvpScreen from 'src/images/amazon_connect_kvp.png'
import CrmAttributeImage from 'src/images/crm_attribute.png'
import CrmButtonImage from 'src/images/crm_button.png'
import { toast } from 'sonner'

type Props = {
  organization: string
  username: string
}

function Config(props: Props) {
  const { status, data, error, refetch } = useGetOrganization({
    organization: props.organization,
    username: props.username,
  })

  if (status === 'loading' || status === 'idle') {
    return <Preloader />
  }

  if (status === 'error') {
    return <ErrorApi message={error} onClick={refetch} />
  }

  if (data.setupStep !== 'finished') {
    toast.warning(`You didn't finish the setup yet.`)
    return null // FIXME: how to handle this case?
  }

  return (
    <>
      <Section title="APP CONFIG">
        <TrackedToggle
          savedStorage={savedAutoReadyLogin}
          label="auto ready login"
          title="application.autoreadyafterlogin"
          tooltip={
            <p>
              When enabled, the status of the users will be automatically set to
              “Available” after logging in.
            </p>
          }
          initialValue={
            data.application.autoreadyafterlogin.toString() === 'true'
          } // FIXME: handle in json response
        />

        <TrackedToggle
          savedStorage={savedLogoutOnLogin}
          label="logout on login"
          title="application.logoutonlogin"
          tooltip={
            <p>
              When enabled, the user will be automatically logged out from
              Mobile Connect when logging in to another device.
            </p>
          }
          initialValue={data.application.logoutonlogin.toLowerCase() === 'true'} // FIXME: handle in response
        />

        {/* 
        <TrackedTextField
          savedStorage={savedChatRegion}
          label="chat region"
          title="application.chatregion"
          initialValue={data.application.chatregion}
        />
        */}
        <TrackedCountryCodeField
          savedStorage={savedCountryCode}
          label="country code"
          title="application.country_code"
          initialValue={data.application.country_code as string}
          tooltip={
            <p>
              This is the default dial-out country code that users will see in
              the Mobile App. We advise to select the country code to which the
              majority of your outbound calls are directed. Users have the
              option in the Mobile App to select a different country code at any
              time when dialling out.
            </p>
          }
        />

        <TrackedNumberField
          savedStorage={savedActivityTimeout}
          label="activity reminder (hours)"
          title="activityTimeout"
          initialValue={data.activityTimeout}
          tooltip={
            <p>
              The amount of hours of inactivity after which the user will be
              automatically logged out. The minimum is 1 hour and the maximum is
              12 hours.
            </p>
          }
          min={1}
          max={data.aws_connect_type === 'SAML' ? 24 * 7 : 12}
        />

        <TrackedToggle
          savedStorage={savedDisabledAutoMarkdone}
          label="auto markdone voice"
          title="application.disabled_auto_markdone"
          tooltip={
            <div className="flex flex-col gap-5">
              <p>
                When enabled (default), each interaction will be automatically
                closed in the app when the interaction ends.
              </p>
              <p>
                When disabled, users will have to manually click “done” in the
                app to close the interaction.
              </p>
            </div>
          }
          initialValue={data.application.disabled_auto_markdone}
        />
      </Section>
      <Section
        title="SPLASH SCREENS"
        tooltip={
          <div className="flex flex-row gap-5">
            <img
              className="w-[180px] object-fit"
              alt="Splash screen"
              src={SplashscreenImage}
            ></img>
            <div className="flex flex-col gap-5">
              <p>
                The splash screen is the screen that a user sees in the case of
                an incoming interaction before deciding to pick up or decline
                the interaction. it is possible to customize the data that the
                user sees.
              </p>
              <p>
                Per operating system, you can choose two values in the splash
                screen. By default, First Name and Last Name are displayed.
              </p>
              <p>Example: Pat Thompson</p>
            </div>
          </div>
        }
      >
        <TrackedTextField
          savedStorage={savedSplashAndroid1}
          label="android splash 1"
          title="application.kvpsandroidsplash.kvp1"
          initialValue={data.application.kvpsandroidsplash.kvp1}
        />

        <TrackedTextField
          savedStorage={savedSplashAndroid2}
          label="android splash 2"
          title="application.kvpsandroidsplash.kvp2"
          initialValue={data.application.kvpsandroidsplash.kvp2}
        />

        <TrackedTextField
          savedStorage={savedSplashIos1}
          label="ios splash 1"
          title="application.kvpsiossplash.kvp1"
          initialValue={data.application.kvpsiossplash.kvp1}
        />

        <TrackedTextField
          savedStorage={savedSplashIos2}
          label="ios splash 2"
          title="application.kvpsiossplash.kvp2"
          initialValue={data.application.kvpsiossplash.kvp2}
        />
      </Section>

      <Section
        title="KVP"
        tooltip={
          <div className="flex flex-row gap-5">
            <img
              className="w-[180px] object-fit"
              alt="Amazon Kvp Screen"
              src={AmazonKvpScreen}
            ></img>
            <div className="flex flex-col gap-5">
              <p>
                Key value pairs are metadata labels that you can add to make it
                easier to identify contacts.{' '}
                <a
                  href="https://docs.aws.amazon.com/connect/latest/adminguide/connect-contact-attributes.html"
                  target="_blank"
                  rel="noreferrer"
                  className="text-blue-500 font-normal break-all cursor-pointer hover:decoration-dotted underline-offset-4 hover:underline hover:text-accent"
                >
                  More information
                </a>
              </p>

              <p>
                It is possible to display these values on the screen while
                handling an interaction. Add keys that you have set up within
                Amazon Connect below to display this information on the screen.
              </p>
            </div>
          </div>
        }
      >
        <Kvps initialValue={data.application.kvps} />
      </Section>

      {data.aws_connect_type !== 'SALESFORCE' && (
        <Section title="CRM">
          <TrackedToggle
            savedStorage={savedCrmButtonEnabled}
            label="CRM button"
            title="application.crm_button_enabled"
            tooltip={
              <div className="flex flex-row gap-5">
                <img
                  className="w-[186px] object-fit"
                  src={CrmButtonImage}
                  alt="CRM button in app"
                />
                <div className="flex flex-col gap-5">
                  <p>
                    show/hide a CRM button in the app during an interaction. The
                    button will only be visible if an actual url is present in
                    the contact attributes.
                  </p>
                </div>
              </div>
            }
            initialValue={data.application.crm_button_enabled}
            labelOn="Show"
            labelOff="Hide"
          />

          <TrackedTextField
            savedStorage={savedCrmButtonKey}
            label="CRM key"
            title="application.crm_button_key"
            initialValue={data.application.crm_button_key}
            tooltip={
              <div className="flex flex-row gap-5">
                <img
                  className="w-[421px] object-fit"
                  src={CrmAttributeImage}
                  alt="Contact Attribute"
                />
                <div className="flex flex-col gap-5">
                  <p>
                    Add the key of the contact attribute you've set up within
                    Amazon Connect (routing -&gt; flows -&gt; set contact
                    attributes) below to configure which url the user can open
                    from within the app.
                  </p>
                </div>
              </div>
            }
          />
        </Section>
      )}
      <TrackedSaveButton
        organization={props.organization}
        username={props.username}
      />
    </>
  )
}

export default function ValidateInfo() {
  const [tokens] = savedTokens.useState(null)

  if (!tokens) {
    return <ErrorNotLoggedIn />
  }

  return (
    <Config organization={tokens.organization} username={tokens.username} />
  )
}
